<template>
  <div class="championship-qualifiers">
    <!-- Controlled empty state when the championship has no qualification
         stage(s) — the bracket may start straight at Round of 16 for some
         clients, so a missing qualifier is a normal, expected case. -->
    <div v-if="!qualifierStages.length" class="qualifiers-empty">
      {{ $t('championship.qualifiers.empty') }}
    </div>

    <div v-else class="qualifiers-frame">
      <!-- Stage selector only matters when a championship has more than one
           qualification stage; with a single qualifier we just show its label. -->
      <div class="qualifiers-toolbar">
        <div v-if="qualifierStages.length > 1" class="stage-pills">
          <button
            v-for="stage in qualifierStages"
            :key="stage.id"
            type="button"
            class="stage-pill"
            :class="{ 'is-active': selectedStageId === stage.id }"
            :disabled="loadingStage"
            @click="selectStage(stage)"
          >
            {{ stageLabel(stage) }}
          </button>
        </div>
        <span v-else class="single-stage-label">{{ stageLabel(qualifierStages[0]) }}</span>

        <!-- Cutoff indicator: a qualifier is a flat-ranking tournament where the
             top N advance into the bracket. This is the one thing that makes it
             read as a qualifier rather than a generic leaderboard. -->
        <span v-if="advanceCount" class="advance-chip">
          <i class="fa-regular fa-arrow-up-right-dots mr-1" aria-hidden="true"></i>
          {{ $t('championship.qualifiers.advanceInfo', { count: advanceCount }) }}
        </span>
      </div>

      <div v-if="loadingStage" class="qualifiers-loading">
        <div class="loading-spinner" aria-hidden="true"></div>
        <span class="loading-text">{{ $t('championship.loading') }}</span>
      </div>

      <Table
        v-else-if="stageTournament && stageTournament.participants && stageTournament.participants.length"
        :items="stageTournament.participants"
        :expandable="false"
        :admin-mode="false"
        :row-status-resolver="playoffStatusFor"
        :row-status-header="$t('championship.qualifiers.playoffsHeader')"
      />

      <div v-else class="qualifiers-empty">
        {{ $t('championship.qualifiers.noParticipants') }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import Table from '@/components/tournament-pos-table/Table.vue';
import TournamentRepository from '@/repositories/tournament';
import { stageLabel } from '@/services/championship.service';

export default {
  name: 'ChampionshipQualifiers',
  components: { Table },
  props: {
    overview: { type: Object, default: null },
    /** Championship participants (derived status + current stage) — marks who made the play-offs. */
    participants: { type: Array, default: () => [] },
  },
  data() {
    return {
      stageTournament: null,
      loadingStage: false,
      previousTournament: null,
      selectedStageId: null,
    };
  },
  computed: {
    // Only QUALIFICATION stages, ordered. Each wraps its own normal tournament
    // (the qualifier competition) whose ranking we render below.
    qualifierStages() {
      return [...(this.overview?.stages || [])]
        .filter((s) => s.stageType === 'QUALIFICATION')
        .sort((a, b) => (a.stageOrder || 0) - (b.stageOrder || 0));
    },
    selectedStage() {
      return this.qualifierStages.find((s) => s.id === this.selectedStageId)
        || this.qualifierStages[0]
        || null;
    },
    // How many participants advance from the selected qualifier into the bracket.
    // Only meaningful for the TOP_N rule (a flat-ranking cutoff); other rules
    // don't map to a "top N" line, so we show nothing rather than mislead.
    advanceCount() {
      const stage = this.selectedStage;
      if (!stage || stage.advancementRule !== 'TOP_N') return null;
      return stage.advanceCount > 0 ? stage.advanceCount : null;
    },
    // True once the backend has promoted this qualifier's winners into the bracket.
    stageAdvanced() {
      return !!this.selectedStage?.advancementCompletedAt;
    },
    // Users who went past the selected qualifier: they sit in a later stage, or
    // finished as champion / runner-up. Source of truth is the championship
    // participants endpoint, not a client-side cutoff.
    advancedUserIds() {
      const stageOrder = this.selectedStage?.stageOrder;
      if (stageOrder == null) return new Set();
      const ids = new Set();
      (this.participants || []).forEach((p) => {
        const past = (p.currentStageOrder ?? 0) > stageOrder
          || p.status === 'CHAMPION' || p.status === 'RUNNER_UP';
        if (past && p.userId != null) ids.add(p.userId);
      });
      return ids;
    },
  },
  watch: {
    selectedStage: {
      immediate: true,
      handler(stage, prev) {
        if (!stage || (prev && prev.id === stage.id)) return;
        if (this.selectedStageId == null) this.selectedStageId = stage.id;
        this.loadStageTournament(stage);
      },
    },
  },
  beforeDestroy() {
    // Restore whatever tournament was in the store before we mounted so the
    // qualifiers tab doesn't leak its tournament data into other views.
    if (this.previousTournament !== null) {
      this.setTournament(this.previousTournament);
    }
  },
  methods: {
    ...mapMutations(['setTournament']),
    /**
     * Row status for the ranking table (SFB-1290). Once the stage was advanced
     * the backend tells us exactly who qualified; while it is still running we
     * show the current top-N (TOP_N rule) as "qualifying" so the cutoff is
     * visible without pretending it is final.
     */
    playoffStatusFor(item, rank) {
      if (this.stageAdvanced) {
        return item?.userId != null && this.advancedUserIds.has(item.userId)
          ? { label: this.$t('championship.qualifiers.qualified'), tone: 'default' }
          : null;
      }
      if (this.advanceCount && rank <= this.advanceCount) {
        return { label: this.$t('championship.qualifiers.qualifying'), tone: 'projected' };
      }
      return null;
    },
    selectStage(stage) {
      if (!stage || this.loadingStage || this.selectedStageId === stage.id) return;
      this.selectedStageId = stage.id;
    },
    stageLabel(stage) {
      if (!stage) return '';
      const key = stage.stageType ? `championship.stage.${stage.stageType}` : '';
      const translated = key ? this.$t(key) : '';
      return translated && translated !== key ? translated : stageLabel(stage);
    },
    async loadStageTournament(stage) {
      if (!stage?.tournamentId) return;
      this.loadingStage = true;
      // Stash the existing tournament once so we can restore it on teardown.
      if (this.previousTournament === null) {
        this.previousTournament = this.$store.state.tournament;
      }
      try {
        // NOTE: uses the authenticated overview endpoint. The public
        // (overview-open) endpoints can't serve this yet — the by-externalId
        // route needs an externalId the championship API doesn't expose, and
        // the by-id route gates on PUBLIC visibility which qualifier tournaments
        // don't have. Making this view truly anonymous is pending backend work.
        const res = await TournamentRepository.getTournamentOverview(stage.tournamentId);
        if (res.status < 400) {
          this.stageTournament = res.data;
          // Table.vue and its children read awards / ranking type / features
          // from $store.state.tournament directly, so commit the qualifier's
          // tournament for correct rendering context.
          this.setTournament(res.data);
        }
      } catch (err) {
        console.error('[championship] loading qualifier tournament failed', err);
      } finally {
        this.loadingStage = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.championship-qualifiers {
  font-family: 'Avenir', sans-serif;
  width: 100%;
}

.qualifiers-frame {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.qualifiers-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.single-stage-label {
  font-size: 14px;
  font-weight: 700;
  color: #1b1b1b;
}

.advance-chip {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  color: var(--championship-accent, #c03131);
  background: rgba(192, 49, 49, 0.08);
  border: 1px solid rgba(192, 49, 49, 0.2);
  border-radius: 16px;
  padding: 4px 12px;
  white-space: nowrap;
}

.stage-pills {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.stage-pill {
  background: transparent;
  border: 1px solid #d0d0d0;
  color: #6a6a6a;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 14px;
  border-radius: 16px;
  cursor: pointer;
  letter-spacing: 0.3px;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;

  &:hover:not(:disabled):not(.is-active) {
    border-color: var(--championship-accent, #c03131);
    color: var(--championship-accent, #c03131);
  }

  &.is-active {
    background: var(--championship-accent, #c03131);
    border-color: var(--championship-accent, #c03131);
    color: #fff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.qualifiers-empty {
  text-align: center;
  color: #9a9a9a;
  padding: 60px 12px;
  font-size: 14px;
}

.qualifiers-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 60px 0;
  color: #6a6a6a;

  .loading-spinner {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 3px solid rgba(0, 0, 0, 0.08);
    border-top-color: var(--championship-accent, #c03131);
    animation: qualifiers-spin 0.85s linear infinite;
  }
}

@keyframes qualifiers-spin {
  to { transform: rotate(360deg); }
}

.theme--dark .championship-qualifiers {
  .single-stage-label { color: #f8f8f8; }
  .advance-chip {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.18);
  }
  .stage-pill {
    border-color: rgba(255, 255, 255, 0.18);
    color: #ababab;

    &.is-active {
      background: var(--championship-accent, #c03131);
      border-color: var(--championship-accent, #c03131);
      color: #fff;
    }
  }
}
</style>
